import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import VideoPlayer from "@/components/VideoPlayer";
import { getFormattedDuration } from "@/util/video-player";
import {
  setFrameCount,
  updateTrackSelectedForSeek,
  updateHighlightedSuperTracks,
  setSelectedTracks,
  setSelectedSuperTracks,
  setSuperTrackGlobalVideoDuration,
  setSuperTracks,
} from "@/store/supertrackSlice";
import { matchApi } from "../../api";
import { cloneDeep } from "lodash";

const getSecondsFromFormattedTime = (time) => {
  if (String(time).includes(".")) {
    return (
      Number(String(time).split(".")[0]) * 60 +
      Number(String(time).split(".")[1])
    );
  }
  return time * 60;
};

const SuperTrackMarkupVideo = ({ posRef, frameStep }) => {
  const token = localStorage.getItem("access");
  const dispatch = useDispatch();
  const { videoId: matchId } = useParams();

  const videoRef = useRef();
  const menuRef = useRef();

  const {
    match,
    trackBoxes,
    fps,
    frameCount,
    selectedTracks,
    doubleClickedTrack,
    isCutActive,
    cutFrame,
    superTrackStartTime,
    superTrackEndTime,
    superTracks,
    selectedSuperTrack,
    selectedSuperTracks,
    teams,
  } = useSelector(({ superTracks }) => superTracks);

  const [selectedTrackBoxes, setSelectedTrackBoxes] = useState({});
  const [menuVisible, setMenuVisible] = useState(false);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [selectedSuperTrackId, setSelectedSuperTrackId] = useState("");
  const [numberVisible, setNumberVisible] = useState(false);
  const [selectedMainTracks, setSelectedMainTracks] = useState([]);
  const [colors, setColors] = useState({});

  // If we click on lens button, it will add selected tracks and goes to -5 secs moment.
  useEffect(() => {
    if (!selectedSuperTrack || !superTracks || !fps) return;

    const superTrack = superTracks[selectedSuperTrack.id];

    if (!superTrack) return;

    const _selectedMainTracks = superTrack.tracks.map(
      ({ track_id }) => track_id
    );
    setSelectedMainTracks(_selectedMainTracks);
    let lastFrame = 0;
    superTrack.tracks.forEach(({ frame_end }) => {
      if (frame_end > lastFrame) lastFrame = frame_end;
    });
    videoRef.current.currentTime =
      (lastFrame * 1) / fps - 5 > 0 ? (lastFrame * 1) / fps - 5 : 0;
  }, [selectedSuperTrack, superTracks, fps]);

  useEffect(() => {
    const newColors = {};
    const teamColors = {
      left_team: "red",
      right_team: "yellow",
      ball: "orange",
    };
    selectedMainTracks?.forEach((trackId) => {
      newColors[trackId] = "yellow";
    });
    if (selectedTrackId) {
      newColors[selectedTrackId] = "red";
    }
    for (const team in teams) {
      for (const id of teams[team]) {
        if (!superTracks[id] || !superTracks[id].tracks) continue;
        const trackIds = superTracks[id].tracks.map((track) => track.track_id);
        for (const trackId of trackIds) {
          newColors[trackId] = teamColors[team];
        }
      }
    }
    setColors(newColors);
  }, [selectedMainTracks, selectedTrackId, teams]);

  useEffect(() => {
    const selected = {};
    if (!trackBoxes) return;
    // Only process tracks that are in selectedTracks to avoid unnecessary memory usage
    selectedTracks.forEach((trackId) => {
      if (trackBoxes[trackId]) {
        selected[trackId] = trackBoxes[trackId];
      }
    });
    setSelectedTrackBoxes(selected);
  }, [trackBoxes, selectedTracks]);

  useEffect(() => {
    if (isCutActive) {
      pause();
    }
  }, [isCutActive]);

  useEffect(() => {
    if (isCutActive) {
      videoRef.current.currentTime = cutFrame * (1 / fps);
    }
  }, [cutFrame, fps, isCutActive]);

  useEffect(() => {
    if (!doubleClickedTrack) return;

    const superTrackFirstFrame = doubleClickedTrack.frame_start ?? 0;

    videoRef.current.currentTime = superTrackFirstFrame * (1 / fps);
    play();

    dispatch(updateTrackSelectedForSeek(null));
  }, [doubleClickedTrack, fps]);

  // This hook is responsible for recalculating possible next candidates of the lost track
  // TODO: This requires refactoring and needs to be moved to somewhere else or think of doing this in reducer.
  useEffect(() => {
    if (!selectedSuperTrack || !superTracks) return;

    const clonedSelectedSuperTracks = cloneDeep(selectedSuperTracks);
    let lastFrame = 0;
    const superTrack = superTracks[selectedSuperTrack.id];

    superTrack.tracks.forEach(({ frame_end }) => {
      if (frame_end > lastFrame) lastFrame = frame_end;
    });
    let highlightedSuperTracks = [];
    for (let id in superTracks) {
      const isPossible = superTracks[id].tracks.every(
        // eslint-disable-next-line no-loop-func
        ({ frame_start }) => frame_start > lastFrame
      );
      if (isPossible) {
        highlightedSuperTracks.push(id);
        clonedSelectedSuperTracks[id] = true;
      } else {
        clonedSelectedSuperTracks[id] = false;
      }
    }
    // select the track itself
    clonedSelectedSuperTracks[selectedSuperTrack.id] = true;
    dispatch(setSelectedSuperTracks({ ...clonedSelectedSuperTracks }));
    dispatch(updateHighlightedSuperTracks(highlightedSuperTracks));

    let selectedTrackIds = [];
    [...highlightedSuperTracks, selectedSuperTrack.id].forEach((id) => {
      const trackIds = superTracks[id].tracks.map(({ track_id }) => track_id);
      selectedTrackIds = [...selectedTrackIds, ...trackIds];
    });
    dispatch(setSelectedTracks(selectedTrackIds));
  }, [superTracks, selectedSuperTrack]);

  const play = useCallback(async () => {
    await videoRef.current.play();
  }, []);

  const pause = useCallback(async () => {
    await videoRef.current.pause();
  }, []);

  const onTimeUpdateHandler = () => {
    if (!posRef.current) return;

    let position =
      Math.round(
        (videoRef.current.currentTime - superTrackStartTime * 60) / (1 / fps)
      ) * 0.5;

    if (position >= frameCount) {
      position = frameCount * 0.5 - superTrackStartTime * 60;
    }

    posRef.current.style.left = `${position - 5}px`;
  };

  const onMouseDownHandler = (e) => {
    if ((e.evt.button === 0 && e.evt.ctrlKey) || e.evt.button === 2) {
      if (!["rect", "text"].includes(e.target?.attrs.name)) {
        // if we are on empty place of the stage we will do nothing
        setMenuVisible(false);
        setSelectedTrackId(null);
        return;
      }
      const { trackId } = e.target.attrs;
      setSelectedTrackId(trackId);

      let superTrackId;
      for (let id in superTracks) {
        const exists = superTracks[id].tracks.find(
          (track) => track.track_id === Number(trackId)
        );
        if (exists) {
          superTrackId = id;
          break;
        }
      }

      setSelectedSuperTrackId(superTrackId);

      const stage = e.target.getStage();
      // show menu
      setMenuVisible(true);
      menuRef.current.style.top = stage.getPointerPosition().y + 4 + "px";
      menuRef.current.style.left = stage.getPointerPosition().x + 4 + "px";
    } else {
      setMenuVisible(false);
      setSelectedTrackId(null);
    }
  };

  const onToggleNumberVisible = useCallback(() => {
    setNumberVisible(!numberVisible);
  }, [numberVisible]);

  const onVideoLoadHandler = () => {
    const totalMinutes = videoRef.current.duration / 60;
    const minutes = Math.floor(totalMinutes);
    const seconds = Math.floor((totalMinutes - minutes) * 60);
    const formattedTime = `${minutes}.${seconds.toString().padStart(2, '0')}`;

    dispatch(setSuperTrackGlobalVideoDuration(+formattedTime));
    dispatch(setFrameCount(Math.round(videoRef.current.duration / (1 / fps))));
  };

  const chooseTeam = async (team) => {
    try {
      if (teams.ball?.length > 0 && team === "ball") {
        toast.warning("You are allowed to pick only one ball track.");
        return;
      }
      const clonedSuperTracks = cloneDeep(superTracks);
      const superTrack = clonedSuperTracks[selectedSuperTrackId];
      if (superTrack) {
        superTrack.kind = team;
      }
      dispatch(
        setSuperTracks({ ...clonedSuperTracks, [superTrack.id]: superTrack })
      );
      setMenuVisible(false);
      await matchApi.postSuperTrackPlayer({
        matchId: matchId,
        superTracksId: selectedSuperTrackId,
        kind: team,
      });
    } catch (err) {
      toast.error("Something went wrong.");
    }
  };

  const onAddTask = async (isBall) => {
    try {
      await matchApi.addTask({
        match: matchId,
        task_type: "supertrack",
        task: {
          isBall: isBall,
          super_track_uid: parseInt(selectedSuperTrackId),
        },
        token: token,
      });
      toast(`Task created`);
      setMenuVisible(false);
    } catch (error) {
      console.error(error);
      toast(`Something went wrong. ${error.message}`);
    }
  };

  return (
    <>
      <Row className="tw-justify-center">
        <Col xs={11} className="tw-relative super-track-video-toolbar">
          <Form.Check
            className={`switch ${numberVisible ? "on" : "off"}`}
            reverse
            inline
            type="switch"
            label="Show IDs:"
            onChange={onToggleNumberVisible}
          />
        </Col>
      </Row>
      <Row className="tw-justify-center">
        <Col xs={11} className="tw-relative super-track-video__container">
          <VideoPlayer
            ref={videoRef}
            src={match.video.origin}
            fps={fps}
            frameStep={frameStep}
            startTime={getSecondsFromFormattedTime(superTrackStartTime)}
            endTime={getSecondsFromFormattedTime(superTrackEndTime)}
            tracks={selectedTrackBoxes}
            colors={colors}
            idVisible={numberVisible}
            onMouseDownHandler={onMouseDownHandler}
            onVideoLoadHandler={onVideoLoadHandler}
            onTimeUpdateHandler={onTimeUpdateHandler}
          />
          <div
            ref={menuRef}
            className={menuVisible ? "tw-absolute z-1" : "d-none"}
          >
            <div className="super-track-video__context-menu">
              {/* <button onClick={onMerge}>Merge</button> */}
              <button onClick={() => chooseTeam("left_team")}>Left Team</button>
              <button onClick={() => chooseTeam("right_team")}>
                Right Team
              </button>
              <button onClick={() => chooseTeam("ball")}>Ball</button>
              <button onClick={() => onAddTask(false)}>Add Task No Ball</button>
            </div>
          </div>
          {/* <ControlledMenu
            anchorPoint={anchorPoint}
            state={isOpen ? "open" : "closed"}
            direction="right"
            onClose={() => setOpen(false)}
          >
            <MenuItem>Cut</MenuItem>
            <MenuItem>Copy</MenuItem>
            <MenuItem>Paste</MenuItem>
          </ControlledMenu> */}
        </Col>
      </Row>
    </>
  );
};

export default SuperTrackMarkupVideo;
